define('salesInboxListView',[
  'module',
  'app',
  'backbone',
  'underscore',
  'jquery',
  'settings',
  'tableView',
  'tableFieldView',
  'salesInboxBarometer',
  'entities/sales_inbox',
  // Registers the `kanban-tickets:get-users` handler used to populate the
  // "Assigned to" filter with the account's users.
  'entities/kanban_tickets'
], function (Module, App, Backbone, _, $, Settings, TableView, FieldView, Barometer) {
  'use strict';

  var FILTER_STORAGE_KEY = 'salesInbox.listFilters';
  var SORT_STORAGE_KEY = 'salesInbox.listSort';

  // Last queue page the user was on, kept in memory for the lifetime of the
  // SPA session so that returning from a detail screen lands back on the same
  // page instead of resetting to page 1. It is updated on every body fetch
  // (so it follows the paginator) and therefore returns to 1 automatically
  // whenever a filter / sort / segment change re-fetches from first = 0. A
  // full browser reload re-evaluates this module and starts fresh at page 1.
  var rememberedPage = 1;

  function currentUserSecId() {
    return (Settings.get('currentUser') && Settings.get('currentUser').secId) || null;
  }

  // Best display name for a userpid from the kanban-tickets:get-users payload.
  function userDisplayName(u) {
    if (!u) { return ''; }
    var full = ((u.firstName || '') + ' ' + (u.lastName || '')).replace(/\s+/g, ' ').trim();
    return u.fullName || full || u.email || u.logonId || u.secId || '';
  }

  // ---- formatting helpers ---------------------------------------------
  // Title-case a backend CONSTANT ("BIG_ORDER_VALUE" -> "Big Order Value").
  function prettyCase(s) {
    if (!s) { return ''; }
    return String(s).replace(/_/g, ' ').toLowerCase()
      .replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }

  // Thousands separator without relying on locale APIs (es3 jshint target).
  function formatThousands(n) {
    var v = Math.round(Number(n) || 0);
    return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Priority badge tier — collapses the raw score into four visual bands
  // that share the urgency ramp (matches the detail header's badge).
  function prioTier(score) {
    var s = Number(score) || 0;
    if (s >= 13) { return 'max'; }
    if (s >= 6)  { return 'hi'; }
    if (s >= 3)  { return 'mid'; }
    return 'lo';
  }

  // Absolute date the email was received, shown alongside the relative SLA
  // pill so the operator can see exactly when it landed (fr-BE, day-first).
  function formatReceived(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  // SLA state for a row, derived purely client-side from receivedAt + the
  // value-scaled SLA window (BIG 2h / MEDIUM 8h / else 24h — the same windows
  // the detail view applies). Returns the colour band and a short countdown
  // label. This is the spine of the queue: colour = time-left weighted by
  // value, so the hottest money rises to the eye without reading a cell.
  function slaState(receivedAt, potential) {
    if (!receivedAt) { return { cls: 'cool', label: '—' }; }
    var ageH = (Date.now() - new Date(receivedAt).getTime()) / 36e5;
    if (isNaN(ageH)) { return { cls: 'cool', label: '—' }; }
    var slaH = potential === 'BIG_ORDER_VALUE' ? 2
             : potential === 'MEDIUM_ORDER_VALUE' ? 8 : 24;
    var rem = slaH - ageH;
    var cls;
    if (rem <= 0) { cls = 'hot'; }
    else {
      var ratio = rem / slaH;
      cls = ratio <= 0.34 ? 'hot' : ratio <= 0.67 ? 'warn' : 'calm';
    }
    return { cls: cls, label: rem <= 0 ? overdueLabel(-rem) : leftLabel(rem) };
  }

  function leftLabel(remH) {
    if (remH >= 24) {
      var d = Math.floor(remH / 24);
      var dh = Math.round(remH - d * 24);
      return d + 'd ' + dh + 'h';
    }
    var h = Math.floor(remH);
    var m = Math.round((remH - h) * 60);
    if (h >= 10) { return h + 'h'; }
    if (h >= 1)  { return h + 'h ' + (m < 10 ? '0' : '') + m + 'm'; }
    return Math.max(1, Math.round(remH * 60)) + 'm';
  }

  function overdueLabel(overH) {
    if (overH >= 24) { return Math.floor(overH / 24) + 'd over'; }
    if (overH >= 1)  { return Math.round(overH) + 'h over'; }
    return Math.round(overH * 60) + 'm over';
  }

  // Two-letter avatar initials from a customer / user name.
  function initials(name) {
    if (!name) { return '?'; }
    var parts = $.trim(name).split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  var CAT_CLASS = {
    SALES: 'sales', DESIGN: 'design', INVOICING: 'invoicing',
    RECLAMATION: 'reclamation', TRACKING: 'tracking',
    NOT_ACTIONABLE: 'not-actionable'
  };

  // The full lifecycle, in the same order the detail header's status <select>
  // offers them — so the quick menu in the queue and the detail picker stay in
  // step.
  var STATUSES = ['NEW', 'AWAITING', 'RESOLVED'];

  // Email-case values grouped by their category (mirrors the backend EmailCase
  // enum). Drives the "Email type" filter, which maps to the emailCase param.
  var EMAIL_CASE_GROUPS = [
    { label: 'Sales', cases: ['PRICE_NEGOTIATION', 'OFFER_DECLINED', 'EXPRESS_DELIVERY', 'STOCKS', 'PURCHASE_ORDER', 'REORDER', 'READY_TO_ORDER', 'DELIVERY_TIME', 'SAMPLE', 'QUANTITY', 'OFFER_REQUEST', 'CHANGE', 'UNFINISHED_ORDER', 'UNDER_REVIEW'] },
    { label: 'Design', cases: ['ASK_FOR_MODIFICATION', 'LOGO_NOT_PRINTABLE', 'NO_LOGO_SENT'] },
    { label: 'Invoicing', cases: ['ASK_FOR_INVOICE', 'PAY_AFTER_DELIVERY', 'CHANGE_DATA', 'ASK_FOR_RIB'] },
    { label: 'Reclamation', cases: ['PRINTING_ISSUE', 'BROKEN_GOODS', 'MISSING_GOODS'] },
    { label: 'Tracking', cases: ['WHEN_WILL_ARRIVE', 'DID_NOT_ARRIVE', 'PROBLEM'] },
    { label: 'Not actionable', cases: ['OUT_OF_OFFICE', 'EMAIL_NOT_DELIVERED', 'CARRIER_SURVEY', 'VENDOR_SOLICITATION'] }
  ];

  // Backend status constant -> the chip modifier class (st-awaiting …).
  function statusClass(status) {
    return (status || 'NEW').toLowerCase().replace(/_/g, '-');
  }

  // Label colours come from Gmail; clamp to a hex value before dropping into
  // an inline style so a malformed colour can't break out of the attribute.
  function safeColor(c, fallback) {
    return (typeof c === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(c)) ? c : fallback;
  }

  function renderLabels(labels) {
    if (!labels || !labels.length) { return ''; }
    return _.map(labels, function (lbl) {
      var bg = safeColor(lbl.backgroundColor, '#eef1f6');
      var fg = safeColor(lbl.textColor, '#5f6368');
      var name = _.escape(lbl.name || lbl.labelId || '');
      return '<span class="si-label-chip" style="background-color:' + bg + ';color:' + fg +
        '" title="' + name + '">' + name + '</span>';
    }).join('');
  }

  // ---------------------------------------------------------------------
  // Queue row — one rich grid cell per inbox item. Rendered as the single
  // field of each table row so the framework's pagination / fetch / count /
  // sort machinery is preserved while the row design is fully custom.
  // Clicks bubble to the row (-> navigate to detail); inner controls stop
  // propagation. Re-renders on any model change (cheap, keeps the cell in
  // sync after an inline assignment).
  // ---------------------------------------------------------------------
  var QueueRowView = FieldView.extend({
    className: 'table-field-view queue-cell',

    template: _.template(
      '<div class="rail r-<%= sla.cls %>"></div>' +
      '<div class="q-prio"><span class="prio p-<%= prioTier %>" title="Priority <%= priorityScore %>"><%= priorityScore %></span></div>' +
      '<div class="q-sla"><span class="sla s-<%= sla.cls %>"><%= sla.label %></span></div>' +
      '<div class="q-identity">' +
      '  <div class="top">' +
      '    <% if (customerVip) { %><span class="vip" title="VIP customer">VIP</span><% } %>' +
      '    <span class="cust" title="<%= customerName %>"><%= customerName %></span>' +
      '    <span class="status-wrap">' +
      '      <span class="status-chip q-status-chip st-<%= statusClass %> interactive" title="Change status"><%= statusLabel %><span class="chip-caret">▾</span></span>' +
      '      <div class="si-status-menu" style="display:none"></div>' +
      '    </span>' +
      '  </div>' +
      '  <div class="subj"><%= subject %><% if (senderEmail) { %> <span class="from">· <%= senderEmail %></span><% } %></div>' +
      '  <% if (receivedLabel) { %><div class="q-when" title="Received"><%= receivedLabel %></div><% } %>' +
      '  <% if (note) { %><div class="q-note" title="<%= note %>"><span class="q-note-ic">🗒</span><%= note %></div><% } %>' +
      '  <% if (labelsHtml) { %><div class="q-labels"><%= labelsHtml %></div><% } %>' +
      '</div>' +
      '<div class="q-cat">' +
      '  <% if (category) { %><span class="cat c-<%= catClass %>"><span class="dot"></span><%= categoryLabel %></span><% } %>' +
      '  <% if (emailCase) { %><span class="cat case"><%= caseLabel %></span><% } %>' +
      '</div>' +
      '<div class="q-val">' +
      '  <span class="value"><span class="cur">€</span><%= potentialValueFmt %></span>' +
      '  <span class="pot-tier <%= potTier %>" title="<%= potentialLabel %>"></span>' +
      '</div>' +
      '<div class="q-assignee">' +
      '  <span class="q-assignee-open" title="Assign to user">' +
      '    <% if (assigneeName) { %><span class="avatar"><%= assigneeInitials %></span><span class="nm"><%= assigneeName %></span>' +
      '    <% } else { %><span class="avatar empty">+</span><span class="nm">Unassigned</span><% } %>' +
      '  </span>' +
      '  <div class="q-actions">' +
      '    <% if (!isMine) { %><button type="button" class="rowbtn si-assign-me">Assign me</button><% } %>' +
      '    <button type="button" class="rowbtn si-assign-other">Assign…</button>' +
      '    <button type="button" class="rowbtn primary si-open">Open</button>' +
      '  </div>' +
      '</div>'
    ),

    events: {
      'click .si-assign-me': 'onAssignMe',
      'click .si-assign-other': 'onOpenAssignee',
      'click .si-open': 'onOpen',
      'click .q-assignee-open': 'onOpenAssignee',
      'click .q-status-chip': 'onStatusChipClick',
      'click .si-status-opt': 'onStatusOptionClick'
    },

    serializeData: function () {
      var m = this.model;
      var assignedSecId = m.get('assignedUserpidSecId');
      var assignedName = m.get('assignedUserpidName');
      var mine = currentUserSecId();
      var isMine = !!(mine && assignedSecId === mine);
      var status = m.get('status') || 'NEW';
      var potential = m.get('potential');

      return {
        priorityScore: Number(m.get('priorityScore')) || 0,
        prioTier: prioTier(m.get('priorityScore')),
        sla: slaState(m.get('receivedAt'), potential),
        customerVip: !!m.get('customerVip'),
        customerName: _.escape(m.get('customerName') || m.get('senderEmail') || 'Unknown'),
        senderEmail: _.escape(m.get('senderEmail') || ''),
        subject: _.escape(m.get('subject') || m.get('emailMessageIdHeader') || '(no subject)'),
        receivedLabel: formatReceived(m.get('receivedAt')),
        note: _.escape(m.get('note') || ''),
        labelsHtml: renderLabels(m.get('labels')),
        category: m.get('category'),
        catClass: CAT_CLASS[m.get('category')] || 'other',
        categoryLabel: prettyCase(m.get('category')),
        emailCase: m.get('emailCase'),
        caseLabel: prettyCase(m.get('emailCase')),
        potentialValueFmt: formatThousands(m.get('potentialValue')),
        potTier: potential === 'BIG_ORDER_VALUE' ? 't-big'
               : potential === 'MEDIUM_ORDER_VALUE' ? 't-mid' : '',
        potentialLabel: potential ? prettyCase(potential) : 'Unassessed',
        statusClass: statusClass(status),
        statusLabel: prettyCase(status),
        assigneeName: assignedName ? (isMine ? 'You' : _.escape(assignedName)) : '',
        assigneeInitials: initials(assignedName),
        isMine: isMine
      };
    },

    // FieldView.onRender runs field.onRender / field.bind; we render the whole
    // cell from serializeData via the template, so there's nothing to bind (the
    // base would otherwise try to bind a single attribute). A re-render (model
    // change after an inline edit) also discards any open status menu, so reset
    // its state and drop the outside-click handler.
    onRender: function () {
      this.closeStatusMenu();
    },

    onDestroy: function () {
      this.closeStatusMenu();
    },

    onOpen: function (e) {
      e.stopPropagation();
      App.navigate('sales-inbox/' + this.model.get('secId'), { trigger: true });
    },

    // Open/close the quick status menu. stopPropagation keeps the row's own
    // click (which opens the item) from firing.
    onStatusChipClick: function (e) {
      e.stopPropagation();
      if (this._statusMenuOpen) { this.closeStatusMenu(); return; }
      this.openStatusMenu();
    },

    openStatusMenu: function () {
      var self = this;
      var current = this.model.get('status') || 'NEW';
      var html = _.map(STATUSES, function (s) {
        var active = s === current;
        return '<button type="button" class="si-status-opt' + (active ? ' active' : '') +
          '" data-status="' + s + '">' +
          '<span class="status-chip st-' + statusClass(s) + '">' + _.escape(prettyCase(s)) + '</span>' +
          '</button>';
      }).join('');
      this.$('.si-status-menu').html(html).show();
      this._statusMenuOpen = true;

      // Close on any click outside the menu. Deferred so the click that opened
      // it doesn't immediately bubble to the document and shut it again.
      this._statusDocClose = function () { self.closeStatusMenu(); };
      _.defer(function () {
        if (self.isDestroyed) { return; }
        $(document).on('click', self._statusDocClose);
      });
    },

    closeStatusMenu: function () {
      this._statusMenuOpen = false;
      var $menu = this.$('.si-status-menu');
      if ($menu.length) { $menu.hide().empty(); }
      if (this._statusDocClose) {
        $(document).off('click', this._statusDocClose);
        this._statusDocClose = null;
      }
    },

    onStatusOptionClick: function (e) {
      e.stopPropagation();
      var status = $(e.currentTarget).attr('data-status');
      this.closeStatusMenu();
      if (status && status !== this.model.get('status')) { this.applyStatusChange(status); }
    },

    // Persist the new status, then patch the model in place so the cell
    // re-renders with the new chip — same patch-in-place approach the assign
    // actions (and the detail header) use, rather than a full list reload.
    applyStatusChange: function (status) {
      var self = this;
      this.$('.q-status-chip').addClass('busy');
      App.request('salesInbox:status', this.model.get('secId'), status).done(function (data) {
        if (self.isDestroyed) { return; }
        if (data && typeof data === 'object') { self.model.set(data); }
        else { self.model.set({ status: status }); }
      }).fail(function () {
        if (self.isDestroyed) { return; }
        self.$('.q-status-chip').removeClass('busy');
      });
    },

    onAssignMe: function (e) {
      e.stopPropagation();
      var self = this;
      var mine = currentUserSecId();
      if (!mine) { return; }
      var $btn = $(e.currentTarget);
      $btn.prop('disabled', true).text('Assigning…');
      App.request('salesInbox:assign-user', this.model.get('secId'), mine).done(function (data) {
        if (data && typeof data === 'object') { self.model.set(data); }
        else { self.model.set({ assignedUserpidSecId: mine }); }
      }).fail(function () {
        $btn.prop('disabled', false).text('Assign me');
      });
    },

    // Open the shared user-selector modal to (re)assign straight from the
    // queue — the same component the detail view uses. On success the row
    // model is updated from the server response so the cell re-renders.
    onOpenAssignee: function (e) {
      e.stopPropagation();
      var self = this;
      var secId = this.model.get('secId');
      var currentSecId = this.model.get('assignedUserpidSecId');
      var currentName = this.model.get('assignedUserpidName');

      require(['userSelectorView'], function (UserSelectorView) {
        var preselected = [];
        if (currentSecId) {
          preselected.push(new Backbone.Model({
            secId: currentSecId, fullName: currentName || '', email: ''
          }));
        }
        UserSelectorView.prototype.open({
          users: preselected,
          multiselect: false,
          onSelected: function (user) {
            var defer = $.Deferred();
            App.request('salesInbox:assign-user', secId, user.get('secId')).done(function (data) {
              if (data && typeof data === 'object') { self.model.set(data); }
              else {
                self.model.set({
                  assignedUserpidSecId: user.get('secId'),
                  assignedUserpidName: user.get('fullName') || ''
                });
              }
              defer.resolve();
            }).fail(function () { defer.reject(); });
            return defer.promise();
          },
          onUnSelected: function () {
            var defer = $.Deferred();
            App.request('salesInbox:assign-user', secId, null).done(function (data) {
              if (data && typeof data === 'object') { self.model.set(data); }
              else { self.model.set({ assignedUserpidSecId: null, assignedUserpidName: '' }); }
              defer.resolve();
            }).fail(function () { defer.reject(); });
            return defer.promise();
          }
        });
      });
    }
  });

  // ---- filter persistence ---------------------------------------------
  function loadFilters() {
    try {
      var raw = window.localStorage && window.localStorage.getItem(FILTER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveFilters(filters) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters || {}));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
    App.trigger('salesInbox:filtersChanged');
  }

  // Segment presets — the rep's real mental queues. Each replaces the
  // assignee/status/vip "scope" while preserving the advanced refinements
  // (potential / category / locale / search). Only filters the backend
  // actually supports are used (see entities/sales_inbox buildFilterParams):
  // there is no SLA-breach filter server-side, so urgency stays visual via
  // the row rail — every segment still surfaces what's breaching.
  var SEGMENTS = [
    { key: 'all',        label: 'All open' },
    { key: 'new',        label: 'New' },
    { key: 'mine',       label: 'Mine' },
    { key: 'unassigned', label: 'Unassigned' },
    { key: 'vip',        label: 'VIP' }
  ];

  Module.exports = TableView.extend({
    className: 'sales-inbox-list-view ' + TableView.prototype.className,
    pageMode: 'pagination',
    pageSize: 50,
    selectable: false,
    header: false, // we render our own segment bar + sticky column header

    initialize: function () {
      this.filters = loadFilters();
      var savedSort = this.loadSort();
      this.sortField = savedSort.sortField || '';
      this.sortDirection = savedSort.sortDirection || 'desc';
      this.segmentCounts = {};
      // Capture the page to restore now, before the framework's first fetch
      // (which runs in onShow) resets `rememberedPage` back to 1.
      this.startPage = rememberedPage > 1 ? rememberedPage : 1;
      if (TableView.prototype.initialize) {
        TableView.prototype.initialize.apply(this, arguments);
      }
    },

    setFields: function () {
      // A single rich cell per row — the queue design lives entirely in
      // QueueRowView. modelEvent:'change' keeps the cell live after an
      // inline assignment merges the server response back into the model.
      return [
        { name: 'queue', className: 'queue-cell', modelEvent: 'change', view: QueueRowView }
      ];
    },

    loadSort: function () {
      try {
        var raw = window.localStorage && window.localStorage.getItem(SORT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (e) { return {}; }
    },

    saveSort: function () {
      try {
        if (window.localStorage) {
          window.localStorage.setItem(SORT_STORAGE_KEY,
            JSON.stringify({ sortField: this.sortField, sortDirection: this.sortDirection }));
        }
      } catch (e) { /* ignore quota / privacy-mode errors */ }
    },

    // Re-sort server-side from page 1.
    setSort: function (field, direction) {
      this.sortField = field;
      this.sortDirection = direction;
      this.saveSort();
      if (this.footerView) { this.footerView.currentPage = 1; }
      if (this.bodyView) {
        this.bodyView.first = 0;
        this.bodyView.reset();
        this.bodyView.fetch();
      }
      if (this.footerView) { this.footerView.fetch(); }
      this.updateSortIndicator();
    },

    onBeforeShow: function () {
      if (TableView.prototype.onBeforeShow) {
        TableView.prototype.onBeforeShow.apply(this, arguments);
      }
      this.renderControls();
    },

    onShow: function () {
      if (TableView.prototype.onShow) {
        TableView.prototype.onShow.apply(this, arguments);
      }
      this.renderColumnHeader();
      this.restoreStartPage();
      this.renderBarometer();
    },

    // Per-locale queue barometer pinned at the very top of the screen. Uses the
    // dedicated, server-cached barometer endpoint (account-wide, server-defined
    // window — independent of the list's own filters) rather than the heavy
    // full-stats call. The host carries the stats screen's class so it inherits
    // the shared barometer styling (tokens + rules live under
    // .sales-inbox-stats-view).
    renderBarometer: function () {
      var self = this;
      App.request('salesInbox:barometer').done(function (data) {
        if (self.isDestroyed) { return; }
        var markup = Barometer.html(Barometer.build(data || {}));
        var $host = self.$el.find('.si-baro-host');
        if (!markup) { $host.remove(); return; }
        if (!$host.length) {
          $host = $('<div class="si-baro-host sales-inbox-stats-view"></div>');
          self.$el.prepend($host);
        }
        $host.html(markup);
      });
    },

    // Jump the freshly-mounted table to the page the user left on. The base
    // onShow has already kicked off a page-1 body fetch; pointing the body at
    // the saved offset and re-fetching aborts that in-flight page-1 request
    // (see fetch()), so only the correct page actually loads. The footer's
    // count fetch is still in flight and will render the paginator against the
    // currentPage we set here once it resolves.
    restoreStartPage: function () {
      var page = this.startPage;
      if (!page || page <= 1) { return; }
      if (this.footerView) { this.footerView.currentPage = page; }
      if (this.bodyView) {
        this.bodyView.first = this.pageSize * (page - 1);
        this.bodyView.fetch();
      }
    },

    onRender: function () {
      if (TableView.prototype.onRender) {
        TableView.prototype.onRender.apply(this, arguments);
      }
      this.setupRowContextMenu();
    },

    // Reset paging + refresh rows and footer count after a filter change.
    applyFilters: function () {
      if (this.footerView) { this.footerView.currentPage = 1; }
      if (this.bodyView) {
        this.bodyView.first = 0;
        this.bodyView.fetch();
      }
      if (this.footerView) { this.footerView.fetch(); }
      this.refreshFilterToggle();
      this.refreshSegmentCounts();
    },

    setupRowContextMenu: function () {
      var self = this;
      this.$el.contextMenu({
        selector: '.table-row-view',
        build: function ($trigger) {
          var row = self.bodyView && self.bodyView.getRow($trigger);
          var email = row && row.model.get('senderEmail');
          if (!email) { return false; }
          return {
            items: {
              filterBySender: {
                name: 'Filter by sender: ' + email,
                callback: function () { self.filterBySender(email); }
              }
            }
          };
        }
      });
    },

    filterBySender: function (email) {
      this.filters.search = email;
      if (this.$searchInput) { this.$searchInput.val(email); }
      this.updateSearchClear();
      saveFilters(this.filters);
      this.applyFilters();
    },

    // ---- which segment is active given the current filters --------------
    activeSegment: function () {
      var f = this.filters || {};
      var mine = currentUserSecId();
      if (mine && f.userSecId === mine) { return 'mine'; }
      if (f.unassigned) { return 'unassigned'; }
      if (f.vip) { return 'vip'; }
      if (f.status === 'NEW') { return 'new'; }
      // A specific status filter (Awaiting / Resolved) is a narrower scope
      // than any tab, so no segment is highlighted.
      if (f.status) { return null; }
      return 'all';
    },

    // Apply a segment preset — replaces the assignee/status/vip scope,
    // preserves potential/category/locale/search.
    selectSegment: function (key) {
      var f = this.filters;
      f.status = null;
      f.userSecId = null;
      f.unassigned = null;
      f.vip = null;
      if (key === 'new')        { f.status = 'NEW'; }
      else if (key === 'mine')  { f.userSecId = currentUserSecId(); }
      else if (key === 'unassigned') { f.unassigned = true; }
      else if (key === 'vip')   { f.vip = true; }
      saveFilters(f);
      this.applyFilters();
      this.updateSegmentBar();
      this.syncAdvancedControls();
    },

    // ---- controls: segment bar + search + advanced disclosure -----------
    renderControls: function () {
      if (this.$el.find('.si-controls').length) { return; }
      var self = this;

      var $bar = $(
        '<div class="si-controls">' +
        '  <div class="si-segments"></div>' +
        '  <div class="si-control-spacer"></div>' +
        '  <div class="si-search">' +
        '    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>' +
        '    <input type="text" class="sales-inbox-search" placeholder="Search sender or subject…"/>' +
        '    <button type="button" class="si-search-clear" title="Clear" aria-label="Clear">&times;</button>' +
        '  </div>' +
        '  <button type="button" class="si-filter-toggle">Filters</button>' +
        '</div>' +
        '<div class="si-advanced" hidden></div>'
      );

      // segments
      var $segs = $bar.filter('.si-controls').find('.si-segments');
      _.each(SEGMENTS, function (seg) {
        var $b = $('<button type="button" class="si-seg" data-seg="' + seg.key + '">' +
          '<span class="lbl">' + seg.label + '</span>' +
          '<span class="n" hidden></span></button>');
        $b.on('click', function () { self.selectSegment(seg.key); });
        $segs.append($b);
      });

      // search
      var $search = $bar.filter('.si-controls').find('.sales-inbox-search');
      $search.val(this.filters.search || '');
      this.$searchInput = $search;
      var $clear = $bar.filter('.si-controls').find('.si-search-clear');
      this.updateSearchClear = function () { $clear.toggle(!!$.trim($search.val())); };
      $clear.on('click', function () {
        $search.val('');
        self.filters.search = null;
        saveFilters(self.filters);
        self.updateSearchClear();
        self.applyFilters();
      });
      $search.on('input', this.updateSearchClear);
      $search.on('input', _.debounce(function () {
        self.filters.search = $(this).val();
        saveFilters(self.filters);
        self.applyFilters();
      }, 300));

      // advanced disclosure
      this.$advanced = $bar.filter('.si-advanced');
      this.$filterToggle = $bar.filter('.si-controls').find('.si-filter-toggle');
      this.buildAdvanced(this.$advanced);
      this.$filterToggle.on('click', _.bind(function () {
        this.setAdvancedOpen(this.$advanced.prop('hidden'));
      }, this));

      this.$el.prepend($bar);
      this.updateSegmentBar();
      this.updateSearchClear();
      this.refreshFilterToggle();
      // Open the advanced panel on load when a refinement is already active
      // (e.g. a persisted Status = Resolved, or a category filter) so
      // it's visible why the list is scoped, rather than silently filtered.
      this.setAdvancedOpen(this.hasPanelFilters());
      this.refreshSegmentCounts();
    },

    // Show/hide the advanced filter panel and keep the toggle button in sync.
    setAdvancedOpen: function (open) {
      if (!this.$advanced) { return; }
      this.$advanced.prop('hidden', !open);
      this.$filterToggle.toggleClass('on', open).text(open ? 'Hide filters' : 'Filters');
      this.refreshFilterToggle();
    },

    // Whether any advanced-panel refinement is active. Status === NEW is
    // excluded: it's represented by the "New" segment tab, not the panel, so
    // it shouldn't force the panel open.
    hasPanelFilters: function () {
      var f = this.filters || {};
      return !!(f.potential || f.category || f.locale || f.myGroups ||
                (f.status && f.status !== 'NEW'));
    },

    // Flag the Filters button when refinements are active so it still signals
    // them while the panel is collapsed.
    refreshFilterToggle: function () {
      if (this.$filterToggle) {
        this.$filterToggle.toggleClass('has-filters', this.hasPanelFilters());
      }
    },

    // Advanced refinements (status / potential / category / locale + My teams)
    // live in a disclosure so the bar stays calm; power users open it when
    // needed. The Status select is how Resolved items are reached —
    // the segment tabs only cover the open queue.
    buildAdvanced: function ($advanced) {
      var self = this;
      $advanced.append(this.buildSelect('status', 'Status', [
        { value: '', label: 'All open' },
        { value: 'NEW', label: 'New' },
        { value: 'AWAITING', label: 'Awaiting' },
        { value: 'RESOLVED', label: 'Resolved' }
      ]));
      $advanced.append(this.buildSelect('potential', 'Potential', [
        { value: '', label: 'Any potential' },
        { value: 'SMALL_ORDER_VALUE', label: 'Small' },
        { value: 'MEDIUM_ORDER_VALUE', label: 'Medium' },
        { value: 'BIG_ORDER_VALUE', label: 'Big' }
      ]));
      $advanced.append(this.buildSelect('category', 'Category', [
        { value: '', label: 'Any category' },
        { value: 'SALES', label: 'Sales' },
        { value: 'DESIGN', label: 'Design' },
        { value: 'INVOICING', label: 'Invoicing' },
        { value: 'RECLAMATION', label: 'Reclamation' },
        { value: 'TRACKING', label: 'Tracking' },
        { value: 'NOT_ACTIONABLE', label: 'Not actionable' }
      ]));
      $advanced.append(this.buildEmailCaseFilter());
      $advanced.append(this.buildSelect('locale', 'Locale', [
        { value: '', label: 'Any locale' },
        { value: 'de', label: 'DE' }, { value: 'fr', label: 'FR' },
        { value: 'nl', label: 'NL' }, { value: 'it', label: 'IT' },
        { value: 'es', label: 'ES' }, { value: 'da', label: 'DA' },
        { value: 'en', label: 'EN' }
      ]));
      $advanced.append(this.buildAssignedUserFilter());

      var $myTeams = $('<label class="si-check"><input type="checkbox"/> My teams</label>');
      $myTeams.find('input').prop('checked', !!this.filters.myGroups).on('change', function () {
        self.filters.myGroups = $(this).is(':checked') ? true : null;
        saveFilters(self.filters);
        self.applyFilters();
      });
      $advanced.append($myTeams);
    },

    buildSelect: function (key, label, options) {
      var self = this;
      var $field = $('<label class="si-adv-field"><span class="k">' + label + '</span></label>');
      var $sel = $('<select class="sales-inbox-filter" data-key="' + key + '"></select>');
      _.each(options, function (opt) {
        var $o = $('<option></option>').val(opt.value).text(opt.label);
        if ((self.filters[key] || '') === opt.value) { $o.prop('selected', true); }
        $sel.append($o);
      });
      $sel.on('change', function () {
        self.filters[key] = $(this).val() || null;
        saveFilters(self.filters);
        self.applyFilters();
        // Changing Status can move which segment (if any) is active.
        self.updateSegmentBar();
      });
      $field.append($sel);
      return $field;
    },

    // The "Email type" filter (emailCase): one select with the cases grouped
    // by category as <optgroup>s. buildSelect only does a flat list, so this
    // is its grouped sibling — same wrapper, data-key and change behaviour.
    buildEmailCaseFilter: function () {
      var self = this;
      var current = self.filters.emailCase || '';
      var $field = $('<label class="si-adv-field"><span class="k">Email type</span></label>');
      var $sel = $('<select class="sales-inbox-filter" data-key="emailCase"></select>');
      $sel.append($('<option></option>').val('').text('Any type'));
      _.each(EMAIL_CASE_GROUPS, function (group) {
        var $g = $('<optgroup></optgroup>').attr('label', group.label);
        _.each(group.cases, function (c) {
          var $o = $('<option></option>').val(c).text(prettyCase(c));
          if (current === c) { $o.prop('selected', true); }
          $g.append($o);
        });
        $sel.append($g);
      });
      $sel.on('change', function () {
        self.filters.emailCase = $(this).val() || null;
        saveFilters(self.filters);
        self.applyFilters();
        self.updateSegmentBar();
      });
      $field.append($sel);
      return $field;
    },

    // "Assigned to" filter (userSecId): a select of the account's users. The
    // select is usable immediately (Any user) and the user options stream in
    // once kanban-tickets:get-users resolves (cached after the first call).
    buildAssignedUserFilter: function () {
      var self = this;
      var current = self.filters.userSecId || '';
      var $field = $('<label class="si-adv-field"><span class="k">Assigned to</span></label>');
      var $sel = $('<select class="sales-inbox-filter" data-key="userSecId"></select>');
      $sel.append($('<option></option>').val('').text('Any user'));
      $sel.on('change', function () {
        self.filters.userSecId = $(this).val() || null;
        saveFilters(self.filters);
        self.applyFilters();
        // "Assigned to me" is just userSecId === currentUser, so a pick here
        // can move which segment is active.
        self.updateSegmentBar();
      });
      $field.append($sel);

      $.when(App.request('kanban-tickets:get-users')).done(function (users) {
        var sorted = _.sortBy(users || [], function (u) {
          return userDisplayName(u).toLowerCase();
        });
        _.each(sorted, function (u) {
          if (!u || !u.secId) { return; }
          var $o = $('<option></option>').val(u.secId).text(userDisplayName(u));
          if (current === u.secId) { $o.prop('selected', true); }
          $sel.append($o);
        });
      });
      return $field;
    },

    // Reflect persisted filters back into the advanced selects (after a
    // segment switch the advanced controls are unchanged, but keep them in
    // sync defensively).
    syncAdvancedControls: function () {
      var self = this;
      this.$el.find('.si-advanced select.sales-inbox-filter').each(function () {
        var key = $(this).attr('data-key');
        $(this).val(self.filters[key] || '');
      });
    },

    updateSegmentBar: function () {
      var active = this.activeSegment();
      this.$el.find('.si-seg').each(function () {
        $(this).toggleClass('on', $(this).attr('data-seg') === active);
      });
    },

    // Best-effort per-segment counts via the same rowCount endpoint the
    // footer uses. Fired on render and after each filter change; failures
    // simply leave the badge hidden.
    refreshSegmentCounts: function () {
      var self = this;
      _.each(SEGMENTS, function (seg) {
        var params = self.segmentParams(seg.key);
        $.ajax({ type: 'GET', url: App.request('salesInbox:count-url', params) })
          .done(function (resp) {
            var n = typeof resp === 'number' ? resp : (resp && resp.count);
            if (n === null || n === undefined) { return; }
            var $n = self.$el.find('.si-seg[data-seg="' + seg.key + '"] .n');
            $n.text(n).prop('hidden', false);
          });
      });
    },

    // Build the count params for a segment: the segment's own scope merged
    // with the active advanced refinements (so "New" counts respect a
    // category filter, etc).
    segmentParams: function (key) {
      var base = {
        potential: this.filters.potential,
        category: this.filters.category,
        locale: this.filters.locale,
        emailCase: this.filters.emailCase,
        search: this.filters.search,
        myGroups: this.filters.myGroups
      };
      if (key === 'new')             { base.status = 'NEW'; }
      else if (key === 'mine')       { base.userSecId = currentUserSecId(); }
      else if (key === 'unassigned') { base.unassigned = true; }
      else if (key === 'vip')        { base.vip = true; }
      return App.request('salesInbox:filter-params', base);
    },

    // ---- sticky column header ------------------------------------------
    // Rendered inside the scrolling body region so it aligns with the rows
    // (same width, same grid template) and stays pinned while they scroll.
    renderColumnHeader: function () {
      var $body = this.$el.find('.body-region');
      if (!$body.length || $body.find('.qhead').length) { return; }
      var self = this;
      var $head = $(
        '<div class="qhead">' +
        '  <div class="h-rail"></div>' +
        '  <div class="h-prio">Prio</div>' +
        '  <div class="h-sla sortable" data-sort="age">' +
        '    <span class="lbl">SLA</span><span class="arrow"></span>' +
        '    <button type="button" class="sort-clear" title="Clear sort" aria-label="Clear sort">&times;</button>' +
        '  </div>' +
        '  <div class="h-identity">Customer / subject</div>' +
        '  <div class="h-cat">Category</div>' +
        '  <div class="h-val sortable" data-sort="potentialValue">' +
        '    <span class="lbl">Potential</span><span class="arrow"></span>' +
        '    <button type="button" class="sort-clear" title="Clear sort" aria-label="Clear sort">&times;</button>' +
        '  </div>' +
        '  <div class="h-assignee">Assignee</div>' +
        '</div>'
      );
      // Click the header to cycle the sort: none -> ascending -> descending ->
      // back to the backend default (priority then recency).
      $head.find('.sortable').on('click', function () {
        var field = $(this).attr('data-sort');
        if (self.sortField !== field) { self.setSort(field, 'asc'); }
        else if (self.sortDirection === 'asc') { self.setSort(field, 'desc'); }
        else { self.clearSort(); }
      });
      // Explicit clear affordance (shown only while the column is sorted) so
      // the default order is reachable without cycling all the way round.
      $head.find('.sort-clear').on('click', function (e) {
        e.stopPropagation();
        self.clearSort();
      });
      $body.prepend($head);
      this.updateSortIndicator();
    },

    // Drop the active sort and return to the backend's default order
    // (sortField '' => buildParams omits sort/dir).
    clearSort: function () {
      if (!this.sortField) { return; }
      this.setSort('', 'desc');
    },

    updateSortIndicator: function () {
      var self = this;
      this.$el.find('.qhead .sortable').each(function () {
        var $col = $(this);
        var field = $col.attr('data-sort');
        var active = self.sortField === field;
        var dir = self.sortDirection;
        var title;
        if (field === 'age') {
          // Age sorts inverted (asc = oldest first), so spell it out.
          title = !active ? 'Sort by SLA' :
            (dir === 'asc' ? 'Sorted by SLA, oldest first — click for newest first' :
                             'Sorted by SLA, newest first — click to clear');
        } else if (field === 'potentialValue') {
          title = !active ? 'Sort by potential value' :
            (dir === 'asc' ? 'Sorted by potential, lowest first — click for highest first' :
                             'Sorted by potential, highest first — click to clear');
        } else {
          title = 'Sort';
        }
        $col.toggleClass('active', active)
          .attr('data-dir', active ? dir : '')
          .attr('title', title);
        $col.find('.arrow').text(active ? (dir === 'asc' ? '▲' : '▼') : '');
      });
    },

    // ---- data ------------------------------------------------------------
    buildParams: function (first, pageSize) {
      var params = App.request('salesInbox:filter-params', this.filters);
      params.first = first;
      params.pageSize = pageSize;
      if (this.sortField) {
        params.sort = this.sortField;
        params.dir = this.sortDirection;
      }
      return params;
    },

    fetch: function (first, pageSize) {
      // Remember the page we're loading so a later detail round-trip can come
      // back to it. Derived from the offset, so paginator clicks, filter/sort
      // resets (first = 0 -> page 1) and the restore path all keep it correct.
      rememberedPage = Math.max(1, Math.floor((Number(first) || 0) / (pageSize || this.pageSize)) + 1);
      var defer = $.Deferred();
      var params = this.buildParams(first, pageSize);
      if (this.fetchRequest) { this.fetchRequest.abort(); }
      this.fetchRequest = $.ajax({
        type: 'GET',
        url: App.request('salesInbox:list-url', params),
        success: function (items) {
          var models = _.map(items || [], function (json) {
            return App.request('salesInbox:model', json);
          });
          defer.resolve(models);
        }
      });
      return defer.promise();
    },

    fetchCount: function () {
      var defer = $.Deferred();
      var params = this.buildParams(0, this.pageSize);
      delete params.first;
      delete params.pageSize;
      if (this.fetchCountRequest) { this.fetchCountRequest.abort(); }
      this.fetchCountRequest = $.ajax({
        url: App.request('salesInbox:count-url', params),
        success: function (resp) { defer.resolve(resp); }
      });
      return defer.promise();
    },

    onRowClick: function (rowView) {
      App.navigate('sales-inbox/' + rowView.model.get('secId'), { trigger: true });
    }
  });
});

